import { ForwardedRef, forwardRef, useImperativeHandle, useRef, useState } from "react";

import { LoadingButton } from "@mui/lab";
import { Typography } from "@mui/material";

import { AlertTypeEnum } from "@/constants";
import { FlightAlertSlogan, HotelAlertSlogan, PanoramaAlertSlogan } from "@/constants/config";
import usePlatform from "@/hooks/usePlatform";

import AlertTab from "../AlertTab";
import { AlertContextProvider } from "./AlertContext";
import AlertResult, { AlertResultProps } from "./AlertResult";
import FlightAlertForm, { IRef } from "./FlightAlertForm";
import HotelAlertForm from "./HotelAlertForm";
import MobileDrawer from "./MobileDrawer";
import PanoramaForm from "./PanoramaForm";
import PcDialog from "./PcDialog";

interface AlertModalProps {
  onSuccess?: () => void;
  onReset?: () => void;
  onShowTips?: () => void;
}

export interface AlertModalRefProps {
  show: (type: AlertTypeEnum, data: any) => void;
}

const AlertFormComponentMap: Record<string, React.FC> = {
  [AlertTypeEnum.Flight]: FlightAlertForm,
  [AlertTypeEnum.Panorama]: PanoramaForm,
  [AlertTypeEnum.Hotel]: HotelAlertForm,
};

const AlertSloganMap: Record<string, string> = {
  [AlertTypeEnum.Flight]: FlightAlertSlogan,
  [AlertTypeEnum.Panorama]: PanoramaAlertSlogan,
  [AlertTypeEnum.Hotel]: HotelAlertSlogan,
};

const AlertModal = forwardRef<AlertModalRefProps, AlertModalProps>((props, ref) => {
  const [alertType, setAlertType] = useState<AlertTypeEnum>(AlertTypeEnum.Flight);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState<number>();
  const [openResult, setOpenResult] = useState(false);
  const [infiniteMode, setInfiniteMode] = useState(false);
  const [alertModalInfo, setAlertModalInfo] = useState<AlertResultProps["data"]>();

  const comRef = useRef<IRef>(null);

  const { isMobile } = usePlatform();

  const handleShow = (type: AlertTypeEnum = AlertTypeEnum.Flight, data: any) => {
    setAlertType(type);
    setOpen(true);

    if (data?.alertId) {
      setEditId(data.alertId);
    }

    setTimeout(() => {
      if (comRef.current) {
        comRef.current.show(data || {});
      }
    }, 300);
  };

  useImperativeHandle(ref, () => ({
    show: handleShow,
  }));

  const renderContent = () => {
    const ComponentName = AlertFormComponentMap[alertType];
    return (
      <div>
        <div className="flex justify-center mb-[10px]">
          <AlertTab value={alertType} onChange={setAlertType} />
        </div>
        <div className="flex justify-center mb-[5px]">
          <Typography>{AlertSloganMap[alertType]}</Typography>
        </div>
        <ComponentName ref={comRef} onReset={props.onReset} onSuccess={props.onSuccess} />
      </div>
    );
  };

  const CreateButton = () => {
    return (
      <LoadingButton
        onClick={handleCreate}
        loading={loading}
        loadingPosition="end"
        endIcon={loading ? <div className="w-[20px]" /> : <div />}
      >
        {editId ? "Modify" : "Create"}
      </LoadingButton>
    );
  };

  const handleCreate = () => {
    if (comRef.current) {
      comRef.current.submit();
    }
  };

  return (
    <AlertContextProvider
      value={{
        alertType,
        loading,
        open,
        isEdit: false,
        openResult,
        setOpenResult,
        alertModalInfo,
        setAlertModalInfo,
        setLoading: (v: boolean) => setLoading(v),
        setOpen,
        infiniteMode,
        setInfiniteMode,
      }}
    >
      <div>
        {isMobile ? (
          <MobileDrawer
            open={open}
            onOpen={setOpen}
            CreateButton={CreateButton()}
            onShowTips={props.onShowTips}
          >
            {renderContent()}
          </MobileDrawer>
        ) : (
          <PcDialog
            open={open}
            onOpen={setOpen}
            CreateButton={CreateButton()}
            onShowTips={props.onShowTips}
          >
            {renderContent()}
          </PcDialog>
        )}

        <AlertResult
          open={openResult}
          onOpen={setOpenResult}
          onNewAlert={() => {
            setOpenResult(false);
            setOpen(true);
          }}
          data={alertModalInfo}
        />
      </div>
    </AlertContextProvider>
  );
});

AlertModal.displayName = "AlertModal";

export default AlertModal;
